import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SignUp as ClerkSignUp } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useClerkAuth';
import { ArrowLeft } from 'lucide-react';
import { InAppBrowserWarning } from '@/components/InAppBrowserWarning';
import SEO from '@/hooks/useSEO';

export default function SignUp() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold">{t('common.loading')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <SEO
        title="Sign Up Free – Start Learning German"
        description="Create your free Lingua Craft account and start reading German articles with translations and practicing citizenship test questions today."
        canonicalPath="/sign-up"
        noindex
      />
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Join Lingua Craft
          </h1>
          <p className="text-muted-foreground mt-2">
            Start your German learning journey today
          </p>
        </div>

        {/* OAuth Redirect Messages */}
        {searchParams.get('from') === 'oauth' &&
          searchParams.get('provider') === 'google' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="space-y-2">
                <p className="text-blue-800 text-sm font-medium">
                  🔄 Redirected from Google Sign-in
                </p>
                <p className="text-blue-700 text-sm">
                  It looks like you tried to sign in with Google but don't have
                  an account yet. Please create your account below, then you can
                  use Google sign-in in the future.
                </p>
              </div>
            </div>
          )}

        {/* General OAuth Redirect */}
        {searchParams.get('from') === 'oauth' &&
          !searchParams.get('provider') && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Create Account:</strong> Please create your account to
                continue with social sign-in.
              </p>
            </div>
          )}

        {/* Legacy OAuth parameter support */}
        {searchParams.get('oauth') === 'google' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Google Account Detected:</strong> It looks like you tried
              to sign in with Google but don't have an account yet. Please
              create your account below using the same Google email, then you
              can sign in with Google in the future.
            </p>
          </div>
        )}

        {/* Sign Up Form */}
        <Card className="bg-card/50 backdrop-blur-sm border-primary/20 p-6">
          {/* Show warning for in-app browsers */}
          <InAppBrowserWarning />

          <div className="flex justify-center">
            <ClerkSignUp
              fallbackRedirectUrl="/"
              signInUrl="/sign-in"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'shadow-none border-0 bg-transparent',
                },
              }}
            />
          </div>
        </Card>

        {/* Sign In Link */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/sign-in"
              className="text-learning-primary hover:text-learning-primary/80 font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
